// Convert CSV / TSV (pasted from a workbook) ↔ forecasting dataset.
//
// Format (one canonical long-format table — also produced by datasetToCsv):
//
//   Type, Project ID, Project, Principal, Contract, Notes, Label, Probability, <Month 1>, <Month 2>, ...
//
//   type        = "scope" or "person"
//   label       = stage name (for scope rows) or person name (for person rows)
//   probability = decimal 0..1 or percentage like 70% (scope rows only)
//   <Months>    = dollar amounts (scope) or load fractions / percentages (person).
//                 People rows accept "40%" or "0.4" — both parse to 0.4.
//
// Project header info (id/name/principal/contract/notes) is repeated on every
// row of that project. The first row encountered for a given Project ID seeds
// the project; later rows MAY leave those fields blank.

const SCOPE_ROW = "scope";
const PERSON_ROW = "person";

// -- delimiter sniff --------------------------------------------------------
export function detectDelimiter(text) {
  const sample = text.split(/\r?\n/).find((l) => l.trim().length > 0) || "";
  if (sample.includes("\t")) return "\t";
  if (sample.includes(",")) return ",";
  if (sample.includes(";")) return ";";
  return ",";
}

// -- minimal CSV parser (quoted fields, escaped quotes) ---------------------
export function parseDelimited(text, delimiter) {
  const d = delimiter || detectDelimiter(text);
  const rows = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; }
        else inQuotes = false;
      } else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === d) { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else if (c === "\r") { /* swallow */ }
      else field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((cell) => String(cell).trim().length > 0));
}

// -- name → id slug ---------------------------------------------------------
const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

// -- value parsers ----------------------------------------------------------
const parseMoney = (s) => {
  if (s === null || s === undefined) return 0;
  const cleaned = String(s).replace(/[$,]/g, "").trim();
  if (!cleaned || cleaned === "—" || cleaned === "-") return 0;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const parseLoad = (s) => {
  if (s === null || s === undefined) return 0;
  const txt = String(s).trim();
  if (!txt || txt === "—" || txt === "-") return 0;
  if (txt.endsWith("%")) return parseFloat(txt.slice(0, -1)) / 100;
  const n = parseFloat(txt);
  if (!Number.isFinite(n)) return 0;
  return n > 1.5 ? n / 100 : n; // tolerate "40" meaning 40%
};

const parseProb = (s) => {
  if (s === null || s === undefined || String(s).trim() === "") return 1;
  const txt = String(s).trim();
  if (txt.endsWith("%")) return parseFloat(txt.slice(0, -1)) / 100;
  const n = parseFloat(txt);
  if (!Number.isFinite(n)) return 1;
  return n > 1.5 ? n / 100 : n;
};

// ---------------------------------------------------------------------------
// CSV / TSV  →  dataset object
// ---------------------------------------------------------------------------
export function csvToDataset(text, opts = {}) {
  const existing = opts.existing || {};
  const rows = parseDelimited(text);
  if (rows.length < 2) throw new Error("Need a header row and at least one data row.");

  const header = rows[0].map((h) => String(h).trim());
  const lower  = header.map((h) => h.toLowerCase());

  const find = (...candidates) => {
    for (const c of candidates) {
      const idx = lower.indexOf(c);
      if (idx !== -1) return idx;
    }
    return -1;
  };
  const iType    = find("type");
  const iProjId  = find("project id", "id", "number", "project number");
  const iName    = find("project", "project name", "name");
  const iPrinc   = find("principal", "project principal");
  const iContract= find("contract", "contract status");
  const iNote    = find("notes", "note");
  const iLabel   = find("label", "scope", "stage", "person", "row label");
  const iProb    = find("probability", "prob.", "prob");

  if (iType === -1 || iProjId === -1 || iLabel === -1) {
    throw new Error('Missing required columns. Need at least "Type", "Project ID", and "Label".');
  }

  // Month columns = everything to the right of probability column (or label if no prob).
  const monthStart = Math.max(iProb, iLabel, iNote, iContract, iPrinc, iName, iProjId, iType) + 1;
  const months = header.slice(monthStart).map((h) => String(h).trim()).filter(Boolean);
  if (months.length === 0) throw new Error("Could not detect month columns to the right of the metadata columns.");

  // Maps
  const existingPrincipalsByName = Object.fromEntries(
    (existing.principals || []).map((p) => [p.name.toLowerCase(), p])
  );
  const existingPeopleByName = Object.fromEntries(
    (existing.people || []).map((p) => [p.name.toLowerCase(), p])
  );

  const principalsOut = [...(existing.principals || [])];
  const peopleOut = [...(existing.people || [])];
  const projectsByPid = new Map();

  const ensurePrincipalId = (rawName) => {
    if (!rawName) return null;
    const k = String(rawName).trim();
    const ex = existingPrincipalsByName[k.toLowerCase()];
    if (ex) return ex.id;
    const id = slug(k);
    if (!principalsOut.find((p) => p.id === id)) {
      principalsOut.push({ id, name: k });
      existingPrincipalsByName[k.toLowerCase()] = { id, name: k };
    }
    return id;
  };

  const ensurePersonId = (rawName) => {
    if (!rawName) return null;
    const k = String(rawName).trim();
    const ex = existingPeopleByName[k.toLowerCase()];
    if (ex) return ex.id;
    let id = slug(k);
    let n = 1;
    while (peopleOut.find((p) => p.id === id)) { n += 1; id = `${slug(k)}_${n}`; }
    const person = { id, name: k, fullTimeCapacity: 1.0 };
    peopleOut.push(person);
    existingPeopleByName[k.toLowerCase()] = person;
    return id;
  };

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r];
    if (!row.length) continue;
    const type = String(row[iType] || "").trim().toLowerCase();
    const pid  = String(row[iProjId] || "").trim();
    if (!type || !pid) continue;

    let project = projectsByPid.get(pid);
    if (!project) {
      project = {
        id: pid,
        name: String(row[iName] || pid).trim(),
        principalId: ensurePrincipalId(row[iPrinc]),
        contract: String(row[iContract] || "Yes").trim(),
        note: String(row[iNote] || "").trim(),
        scopes: [],
        allocations: [],
      };
      projectsByPid.set(pid, project);
    } else {
      // Fill in any header fields that arrive on later rows.
      if (!project.name && row[iName])      project.name = String(row[iName]).trim();
      if (!project.principalId && row[iPrinc]) project.principalId = ensurePrincipalId(row[iPrinc]);
      if (!project.contract && row[iContract]) project.contract = String(row[iContract]).trim();
      if (!project.note && row[iNote])      project.note = String(row[iNote]).trim();
    }

    if (type === SCOPE_ROW) {
      const stage = String(row[iLabel] || "").trim();
      const prob  = parseProb(row[iProb]);
      const revenue = {};
      months.forEach((m, mi) => { revenue[m] = parseMoney(row[monthStart + mi]); });
      project.scopes.push({
        id: `${pid}_s${project.scopes.length + 1}`,
        stage,
        probability: prob,
        revenue,
      });
    } else if (type === PERSON_ROW) {
      const personId = ensurePersonId(row[iLabel]);
      if (!personId) continue;
      const loadObj = {};
      months.forEach((m, mi) => { loadObj[m] = parseLoad(row[monthStart + mi]); });
      project.allocations.push({ personId, load: loadObj });
    }
  }

  const baseMeta = existing.meta || {};
  return {
    meta: {
      firm: baseMeta.firm || "Monk Mackenzie",
      reportDate: baseMeta.reportDate || new Date().toISOString().slice(0, 10),
      currency: baseMeta.currency || "NZD",
      hoursPerFte: baseMeta.hoursPerFte || 40,
      stages: baseMeta.stages,
      months,
    },
    people: peopleOut,
    principals: principalsOut,
    projects: Array.from(projectsByPid.values()),
  };
}

// ---------------------------------------------------------------------------
// dataset  →  CSV template (round-trips via csvToDataset)
// ---------------------------------------------------------------------------
const escapeCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function datasetToCsv(data) {
  if (!data) return "";
  const months = data.meta?.months || [];
  const principalById = Object.fromEntries((data.principals || []).map((p) => [p.id, p.name]));
  const peopleById = Object.fromEntries((data.people || []).map((p) => [p.id, p.name]));
  const header = ["Type", "Project ID", "Project", "Principal", "Contract", "Notes", "Label", "Probability", ...months];
  const rows = [header];
  for (const proj of data.projects || []) {
    const principalName = principalById[proj.principalId] || "";
    for (const s of proj.scopes || []) {
      rows.push([
        "scope", proj.id, proj.name, principalName, proj.contract, proj.note || "",
        s.stage, s.probability ?? 1,
        ...months.map((m) => s.revenue?.[m] ?? 0),
      ]);
    }
    for (const a of proj.allocations || []) {
      rows.push([
        "person", proj.id, "", "", "", "",
        peopleById[a.personId] || a.personId, "",
        ...months.map((m) => a.load?.[m] ?? 0),
      ]);
    }
  }
  return rows.map((r) => r.map(escapeCell).join(",")).join("\n");
}
